import { ref, onUnmounted } from "vue";
import type { Socket } from "socket.io-client";
import { io } from "socket.io-client";
import { logger } from "~/utils/logger";

/**
 * useMembershipSocket — temps réel des compteurs d'adhérents sur la page
 * publique /a/[slug] (miroir de useCampaignSocket pour les cagnottes).
 *
 * Le client rejoint la room publique `membership:{groupId}` (groupId =
 * plan_group_id = id du tarif leader) et reçoit `membership:update` avec les
 * member_count ACTIFS par tarif, poussés par l'API à chaque confirmation /
 * annulation / expiration d'adhésion.
 *
 * Remplace l'ancien polling 30s qui, en repassant par GET
 * /api/public/memberships/:slug, incrémentait view_count à chaque tick
 * (compteur de vues gonflé : 1 onglet ouvert = +2 « vues »/min). Karim 2026-08-04.
 */

export interface MembershipUpdateData {
  groupId: string;
  plans: Array<{ id: string; member_count: number }>;
  timestamp: string;
}

export function useMembershipSocket() {
  const socket = ref<Socket | null>(null);
  const isConnected = ref(false);
  const currentGroupId = ref<string | null>(null);
  const config = useRuntimeConfig();

  const connect = () => {
    if (socket.value?.connected) {
      return socket.value;
    }

    const socketUrl = config.public.apiBaseUrl || "http://localhost:4000";

    socket.value = io(socketUrl, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      withCredentials: true,
      autoConnect: true,
    });

    socket.value.on("connect", () => {
      isConnected.value = true;
      // Re-join automatique après reconnexion (coupure réseau, redéploiement API).
      if (currentGroupId.value) {
        socket.value!.emit("join:membership", currentGroupId.value);
      }
    });

    socket.value.on("disconnect", () => {
      isConnected.value = false;
    });

    socket.value.on("connect_error", (error) => {
      logger.error("WebSocket membership connect error:", error.message);
    });

    return socket.value;
  };

  const joinMembershipRoom = (groupId: string) => {
    currentGroupId.value = groupId;

    if (!socket.value) return;

    if (!socket.value.connected) {
      socket.value.once("connect", () => {
        socket.value!.emit("join:membership", groupId);
      });
      return;
    }

    socket.value.emit("join:membership", groupId);
  };

  const leaveMembershipRoom = (groupId?: string) => {
    const idToLeave = groupId || currentGroupId.value;
    if (!socket.value || !idToLeave) return;

    socket.value.emit("leave:membership", idToLeave);
    if (idToLeave === currentGroupId.value) {
      currentGroupId.value = null;
    }
  };

  const onMembershipUpdate = (
    callback: (data: MembershipUpdateData) => void,
  ) => {
    if (!socket.value) return;
    socket.value.on("membership:update", (data: MembershipUpdateData) => {
      callback(data);
    });
  };

  const disconnect = () => {
    if (socket.value) {
      if (currentGroupId.value) {
        socket.value.emit("leave:membership", currentGroupId.value);
      }
      socket.value.disconnect();
      socket.value = null;
      isConnected.value = false;
      currentGroupId.value = null;
    }
  };

  onUnmounted(() => {
    disconnect();
  });

  return {
    socket,
    isConnected,
    currentGroupId,
    connect,
    joinMembershipRoom,
    leaveMembershipRoom,
    onMembershipUpdate,
    disconnect,
  };
}
